import type {
	FormKitSchemaComponent,
	FormKitSchemaDOMNode,
	FormKitSchemaNode,
} from "@formkit/core";
import type {
	XsdAttribute,
	XsdComplexType,
	XsdElement,
	XsdSchema,
	XsdSimpleType,
} from "@loanlink-nl/hdn-sdk-js/types";
import type { Ref } from "vue";
import { computed, toValue } from "vue";
import {
	wrapInOptionalSectionCard,
	wrapInRepeaterSectionCard,
	wrapInSectionCard,
} from "@/helpers/application-form/schemaBuilding";
import { hdnListToOptions } from "@/helpers/hdnHelpers";
import type { InventarisationSelectOption } from "@/models/inventarisation/InventarisationSelectOption";

interface FormKitSchemaOverrider {
	byType: (type: string) => FormKitFieldOverrides;
	byName: (name: string) => FormKitFieldOverrides;
	byTypeAndName: (type?: string, name?: string) => FormKitFieldOverrides;
}

interface FormKitSchemaOverriderOpts {
	byName?: Record<string, FormKitFieldOverrides>;
	byType?: Record<string, FormKitFieldOverrides>;
}

export const formKitSchemaOverrider = (
	overrides: FormKitSchemaOverriderOpts,
): FormKitSchemaOverrider => ({
	byType: (type) => overrides.byType?.[type] ?? {},
	byName: (name) => overrides.byName?.[name] ?? {},
	byTypeAndName: (type, name) => ({
		...(type ? overrides.byType?.[type] : {}),
		...(name ? overrides.byName?.[name] : {}),
	}),
});

interface FormKitFieldOverrides {
	omit?: boolean;
	hidden?: boolean;
	label?: string | { one: string; many: string };
	$formkit?: string;
	class?: string;
	sorted?: boolean;
	props?: Record<string, any>;
	actions?: boolean;
	fields?: (string | string[])[];
	fns?: Record<string, (...args: any[]) => any>;
}

export function addEmptyOption<T extends string>(
	options: InventarisationSelectOption<T>[],
) {
	return [{ value: undefined, label: selectPlaceholder }, ...options];
}

export const selectPlaceholder = "selecteren...";

function isNumeric(type: string) {
	return [
		"byte",
		"integer",
		"int",
		"short",
		"long",
		"nonNegativeInteger",
		"negativeInteger",
		"positiveInteger",
		"nonPositiveInteger",
		"unsignedLong",
		"unsignedInt",
		"unsignedShort",
		"unsignedByte",
		"decimal",
		"double",
		"float",
	].includes(type);
}

function camelCaseToTitle(text: string): string {
	if (text.endsWith("JN")) {
		return `${camelCaseToTitle(text.slice(0, -2))} (J/N)`;
	}

	if (text.endsWith("Omschr")) {
		return `Omschrijving ${camelCaseToTitle(text.slice(0, -6)).toLowerCase()}`;
	}

	if (text.endsWith("Nr")) {
		const substr = text.substring(0, text.length - 2);

		// If substr has only one capital letter
		if (substr.match(/[A-Z]/g)?.length === 1) {
			return `${substr}nummer`;
		}
	}

	return (
		text
			// Insert a space before all capital letters that are followed by lowercase (to preserve acronyms)
			.replace(/([a-z])([A-Z])/g, "$1 $2")
			// Capitalize the first letter of each word, lower the rest unless the whole word is uppercase
			.replace(/\b\w+\b/g, (word) =>
				word.toUpperCase() === word
					? word
					: word[0]?.toUpperCase() + word.substring(1).toLowerCase(),
			)
	);
}

function btn(icon: string, attrs: Record<string, any>) {
	attrs.class ??= "";
	attrs.class += " secondary header-button";

	return {
		$el: "button",
		attrs: {
			type: "button",
			...attrs,
		},
		children: [
			{
				$el: "span",
				attrs: {
					"aria-hidden": true,
					class: "ht-icon btn-icon",
				},
				children: icon,
			},
		],
	};
}

function findType(
	of: XsdElement | XsdAttribute,
	schema: XsdSchema,
): [XsdComplexType, "complex"] | [XsdSimpleType, "simple"] {
	if ("type" in of) {
		if (schema.simpleType?.[of.type]) {
			const type = schema.simpleType[of.type];
			if (!type) {
				throw new Error(`SimpleType ${of.type} not found in schema`);
			}
			return [type, "simple"];
		}
		if (schema.complexType?.[of.type]) {
			const type = schema.complexType[of.type];
			if (!type) {
				throw new Error(`ComplexType ${of.type} not found in schema`);
			}
			return [type, "complex"];
		}

		// Our type is probably a builtin xsd type
		return [{ restriction: { base: of.type } }, "simple"];
	}

	return [of.simpleType, "simple" as const];
}

export function xsdSchemaToFormkitSchema(
	xsd?: XsdSchema,
	overrider?: FormKitSchemaOverrider,
): { schema: FormKitSchemaNode[] } {
	if (!xsd?.element) {
		return { schema: [] };
	}

	const schema: FormKitSchemaNode[] = Object.values(xsd.element)
		.flatMap((element) => xsdElementToFormkitSchema(element, xsd, 0))
		.filter((field) => field !== null) as FormKitSchemaNode[];

	return { schema };

	function getElementOverrides(
		element: XsdElement | XsdAttribute,
	): FormKitFieldOverrides {
		const namedOverrides = overrider?.byName(element.name);
		const typedOverrides =
			"type" in element ? overrider?.byType(element.type) : undefined;

		return {
			...(namedOverrides ?? {}),
			...(typedOverrides ?? {}),
		};
	}

	function xsdAttributeToFormKitSchema(
		attribute: XsdAttribute,
		schema: XsdSchema,
	): FormKitSchemaNode | null {
		const [type, complexOrSimple] = findType(attribute, schema);

		if (complexOrSimple === "complex") {
			throw new Error(
				`ComplexType found in attribute. Is this even allowed? : ${JSON.stringify(
					attribute,
					null,
					2,
				)}`,
			);
		}

		return xsdSimpleTypeToFormKitSchema(type, attribute);
	}

	function xsdComplexTypeToFormkitSchema(
		type: XsdComplexType,
		element: XsdElement,
		schema: XsdSchema,
		nestLevel: number,
	): FormKitSchemaNode | FormKitSchemaNode[] | null {
		const elementOverrides = getElementOverrides(element);
		if (elementOverrides.omit) {
			return null;
		}

		const xsdFields = [] as (
			| [XsdElement, "element"]
			| [XsdAttribute, "attribute"]
		)[];

		if (type.attribute) {
			xsdFields.push(
				...type.attribute.map(
					(attr) => [attr, "attribute"] as [XsdAttribute, "attribute"],
				),
			);
		}

		if (type.sequence) {
			xsdFields.push(
				...type.sequence.map(
					(child) => [child, "element"] as [XsdElement, "element"],
				),
			);
		}

		let typeFields: FormKitSchemaNode[] = [];

		if (elementOverrides.fields) {
			typeFields = typeFields.concat(
				elementOverrides.fields
					.map((fieldNameOrRow) => {
						if (Array.isArray(fieldNameOrRow)) {
							const fieldRow = fieldNameOrRow;

							return {
								$el: "div",
								attrs: {
									class: "row",
								},
								children: fieldRow
									.map((fieldName) => {
										const fieldElementIndex = xsdFields.findIndex(
											([f]) => f.name === fieldName,
										);
										if (fieldElementIndex === -1) {
											// eslint-disable-next-line no-console
											console.warn(`Field not found: ${fieldName}`);
											return null;
										}

										const fieldElement = xsdFields[fieldElementIndex];
										if (!fieldElement) {
											// eslint-disable-next-line no-console
											console.warn(`Field not found: ${fieldName}`);
											return null;
										}

										xsdFields.splice(fieldElementIndex, 1);

										if (fieldElement[1] === "element") {
											return xsdElementToFormkitSchema(
												fieldElement[0],
												schema,
												nestLevel + 1,
											);
										}

										return xsdAttributeToFormKitSchema(fieldElement[0], schema);
									})
									.filter((field) => field !== null),
							};
						}
						const fieldName = fieldNameOrRow;
						const fieldElementIndex = xsdFields.findIndex(
							([f]) => f.name === fieldName,
						);
						if (fieldElementIndex === -1) {
							// eslint-disable-next-line no-console
							console.warn(`Field not found: ${fieldName}`);
							return null;
						}

						const fieldElement = xsdFields[fieldElementIndex];
						if (!fieldElement) {
							// eslint-disable-next-line no-console
							console.warn(`Field not found: ${fieldName}`);
							return null;
						}

						xsdFields.splice(fieldElementIndex, 1);

						if (fieldElement[1] === "element") {
							return xsdElementToFormkitSchema(
								fieldElement[0],
								schema,
								nestLevel + 1,
							);
						}

						return xsdAttributeToFormKitSchema(fieldElement[0], schema);
					})
					.filter((field) => field !== null) as FormKitSchemaNode[],
			);
		}

		typeFields = typeFields.concat(
			xsdFields
				.map(([field, type]) => {
					if (type === "element") {
						return xsdElementToFormkitSchema(field, schema, nestLevel + 1);
					}

					return xsdAttributeToFormKitSchema(field, schema);
				})
				.filter((field) => field !== null) as FormKitSchemaNode[],
		);

		const isOptional = element.minOccurs === 0;
		const isCollection = (element.maxOccurs ?? 1) > 1;

		let typeChildren: FormKitSchemaNode[] = [];

		const props = elementOverrides.props ?? {};

		if (typeof elementOverrides.label === "string") {
			props.label = elementOverrides.label;
		} else if (typeof elementOverrides.label === "object") {
			props.label = {
				if: "$value.length >= 2",
				then: elementOverrides.label.many,
				else: elementOverrides.label.one,
			};
		}

		props.label ??= camelCaseToTitle(element.label);

		if (
			typeFields.length === 1 &&
			typeof typeFields[0] === "object" &&
			"$cmp" in typeFields[0] &&
			typeFields[0].props.label === "IDREF"
		) {
			const idrefNode: FormKitSchemaNode = {
				$formkit: "IDREF",
				id: props.id,
				name: element.name,
				label: props.label,
				props,
			};

			if ("type" in element) {
				idrefNode["data-xsd"] = element.type;
			}

			if (!isOptional) {
				idrefNode.validation = "*required";
			}

			return idrefNode;
		}

		if (nestLevel > 0) {
			const headingLevel = Math.min(nestLevel + 1, 6);

			const entityHeader: FormKitSchemaDOMNode = {
				$el: "div",
				attrs: {
					class: "header-row",
					"data-level": nestLevel,
					"data-errors": "$state.errors",
					"data-invalid": "$state.valid === false",
					id: "$id",
				},
				children: [
					{
						$el: `h${headingLevel}`,
						children: "$label",
						attrs: {
							class: "hdn-form-heading",
						},
					},
				],
			};

			const showActions = elementOverrides.actions ?? true;

			if (
				isOptional &&
				!isCollection &&
				Array.isArray(entityHeader.children) &&
				showActions
			) {
				entityHeader.children.push(
					btn("delete", {
						"aria-label": `'Verwijder ' + $node.parent.context.label`,
						onClick: "$fns.disableGroup",
					}),
				);
			}

			if (isCollection && Array.isArray(entityHeader.children) && showActions) {
				entityHeader.children.push(
					btn("delete", {
						"aria-label": `$: 'Verwijder ' + $node.parent.context.label + ' (' + ($index + 1) + ')'`,
						onClick: "$node.parent.context.fns.createRemover($index)",
						disabled: "$node.parent.value.length <= $node.parent.props.min",
					}),
					btn("add", {
						"aria-label": `$: 'Voeg ' + $node.parent.context.label + ' toe'`,
						onClick: "$node.parent.context.fns.createAppend()",
						disabled: "$node.parent.value.length >= $node.parent.props.max",
					}),
				);
			}

			// only add entity header for entries that are not top level
			// at top level this functionality is handled by card components
			if (nestLevel !== 1) {
				typeFields.unshift(entityHeader);
			}
		}

		const complexTypeFormKitType = elementOverrides.$formkit;

		typeChildren.push({
			$el: "fieldset",
			children: typeFields,
			label: "$node.parent.context.label",
			attrs: {
				"data-type": "group",
				"data-level": nestLevel,
			},
		});

		// wrap fields in section card with appropriate controls
		if (nestLevel === 1) {
			if (isCollection) {
				typeChildren = [wrapInRepeaterSectionCard(typeChildren)];
			} else if (isOptional) {
				typeChildren = [wrapInOptionalSectionCard(typeChildren)];
			} else {
				typeChildren = [wrapInSectionCard(typeChildren)];
			}

			typeChildren = [
				{
					$el: "fieldset",
					attrs: { id: "$id" },
					children: typeChildren,
				},
			];
		}

		const isHidden = elementOverrides.hidden;
		const headingLevel = Math.min(nestLevel + 1, 6);

		if (isCollection) {
			const repeater: FormKitSchemaNode = {
				$formkit: complexTypeFormKitType ?? "repeater",
				props,
				id: props.id,
				attrs: { id: "$id" },
				label: props.label,
				name: element.name,
				children: typeChildren,
				addButton: false,
				upControl: false,
				downControl: false,
				removeControl: false,
				min: element.minOccurs ?? 1,
				max: element.maxOccurs ?? undefined,
				__raw__sectionsSchema: {
					prefix: {
						if: "$value.length === 0",
						$el: "div",
						attrs: {
							class: "header-row empty",
							id: "$id",
						},
						children: [
							{
								$el: `h${headingLevel}`,
								children: [
									"Voeg ",
									{ $el: "strong", children: "$label" },
									" toe",
								],
								attrs: {
									class: "hdn-form-heading",
								},
							},
							btn("add", {
								"aria-label": `$: 'Voeg ' + $label + ' toe'`,
								onClick: "$node.context.fns.createAppend()",
							}),
						],
					},
				},
			};

			if ("type" in element) {
				repeater["data-xsd"] = element.type;
			}

			if (nestLevel === 1) {
				repeater["data-collapsible"] = true;
			}

			if (isHidden) {
				repeater.$formkit = "meta";
			}

			return repeater;
		}
		const groupType = isOptional ? "optionalGroup" : "group";
		const $formkit = complexTypeFormKitType ?? groupType;

		const group: FormKitSchemaNode = {
			$formkit,
			name: element.name,
			label: props.label,
			id: props.id,
			attrs: { id: "$id" },
			props,
		};

		if (!complexTypeFormKitType) {
			group.children = typeChildren;
		}

		if ("type" in element) {
			group["data-xsd"] = element.type;
		}

		if (nestLevel === 1) {
			group["data-collapsible"] = true;
		}

		if ($formkit === "optionalGroup") {
			group.__raw__sectionsSchema = {
				prefix: {
					$el: "div",
					attrs: {
						class: "header-row empty",
						"data-level": nestLevel,
						id: "$id",
					},
					children: [
						{
							$el: `h${headingLevel}`,
							children: [
								"Voeg ",
								{ $el: "strong", children: "$label" },
								" toe",
							],
							attrs: {
								class: "hdn-form-heading",
							},
						},
						btn("add", {
							"aria-label": `$: 'Voeg ' + $label + ' toe`,
							onClick: "$fns.enableGroup",
						}),
					],
				},
			};
		}

		if (isHidden) {
			group.$formkit = "meta";
		}

		return group;
	}

	function xsdSimpleTypeToFormKitSchema(
		type: XsdSimpleType,
		parent: XsdElement | XsdAttribute,
	): FormKitSchemaNode | null {
		const elementOverrides = getElementOverrides(parent);
		if (elementOverrides.omit) {
			return null;
		}

		const simpleFieldProps: Record<string, any> = {
			name: parent.name,
			sectionsSchema: {
				outer: {
					attrs: {
						onmousedown: "$focusField",
					},
				},
			},
		};

		if ("annotation" in parent && parent.annotation?.documentation) {
			simpleFieldProps.help = parent.annotation.documentation;
		} else if (type.annotation?.documentation) {
			simpleFieldProps.help = type.annotation.documentation;
		}

		if (elementOverrides.label) {
			simpleFieldProps.label = elementOverrides.label;
		} else if ("label" in parent) {
			simpleFieldProps.label = camelCaseToTitle(parent.label);
		} else if (parent.type === "IDREF") {
			simpleFieldProps.label = parent.type;
		}

		if (type.restriction.base === "boolean") {
			simpleFieldProps.type = "checkbox";
		}

		if (type.restriction.enumeration) {
			simpleFieldProps.type = "select";
			simpleFieldProps.options = [];

			const isBooleanType = "type" in parent && parent.type === "BooleanType";
			if (isBooleanType) {
				simpleFieldProps.type = "removableRadio";
			}

			let parsedOptions: { value: string | undefined; label: string }[] =
				hdnListToOptions(type.restriction.enumeration, {
					shouldSort: elementOverrides.sorted,
				});

			if (!isBooleanType) {
				parsedOptions = addEmptyOption(parsedOptions);
			}

			simpleFieldProps.options = simpleFieldProps.options.concat(parsedOptions);

			simpleFieldProps.selectionRemovable = false;
			simpleFieldProps.openOnClick = true;

			simpleFieldProps.sectionsSchema.outer.attrs.onmouseup =
				simpleFieldProps.sectionsSchema.outer.attrs.onmousedown;
			delete simpleFieldProps.sectionsSchema.outer.attrs.onmousedown;
		}

		if (
			"annotation" in parent &&
			typeof parent.annotation?.documentation === "string"
		) {
			simpleFieldProps.info = parent.annotation.documentation;
		}

		if (isNumeric(type.restriction.base)) {
			simpleFieldProps.bind = "$numericField";
		}

		if ("type" in parent && parent.type === "N12_2Type") {
			simpleFieldProps.type = "loanApplicationFormInputCurrency";
		}

		if ("type" in parent && parent.type === "N3_2Type") {
			simpleFieldProps.type = "loanApplicationFormInputPercentage";
		}

		if (elementOverrides.hidden) {
			simpleFieldProps.type = "hidden";
		}

		const validationRules: [string, ...any][] = [];

		switch (type.restriction.base) {
			case "byte":
			case "integer":
			case "int":
			case "short":
			case "long":
			case "nonNegativeInteger":
			case "negativeInteger":
			case "positiveInteger":
			case "nonPositiveInteger":
			case "unsignedLong":
			case "unsignedInt":
			case "unsignedShort":
			case "unsignedByte":
				validationRules.push(["integer"]);
				break;
			case "decimal":
			case "double":
			case "float":
				validationRules.push(["number"]);
				break;
		}

		if (type.restriction.base === "nonNegativeInteger") {
			validationRules.push(["min", 0]);
		}

		if (type.restriction.base === "negativeInteger") {
			validationRules.push(["maxExclusive", 0]);
		}

		if (type.restriction.base === "positiveInteger") {
			validationRules.push(["minExclusive", 0]);
		}

		if (type.restriction.base === "nonPostiveInteger") {
			validationRules.push(["max", 0]);
		}

		const isRequired: boolean =
			!("minOccurs" in parent) ||
			(parent.minOccurs ?? 1) > 0 ||
			("use" in parent && parent.use === "required");

		if (isRequired) {
			validationRules.push(["required"]);

			if (type.restriction.enumeration) {
				validationRules.push(["not", selectPlaceholder]);
				simpleFieldProps.validationMessages = {
					not: ({ name }: { name: string }) => `${name} is verplicht.`,
				};
			}
		}

		if (type.restriction.maxLength) {
			validationRules.push(["length", 0, type.restriction.maxLength]);
		}

		const min = type.restriction.minInclusive;
		const max = type.restriction.maxInclusive;

		if (min && max) {
			validationRules.push(["between", min, max]);
		} else if (min) {
			validationRules.push(["min", min]);
		} else if (max) {
			validationRules.push(["max", max]);
		}

		if (type.restriction.pattern) {
			validationRules.push([
				"matches",
				new RegExp(`^${type.restriction.pattern}$`),
			]);
		}

		if ("type" in parent && parent.type === "IBANType") {
			validationRules.push(["?iban"]);
		}

		simpleFieldProps.validation = validationRules.map((rule) => [
			`*${rule[0]}`,
			...rule.slice(1),
		]);

		if (elementOverrides.$formkit) {
			simpleFieldProps.type = elementOverrides.$formkit;
		}

		const props = { ...simpleFieldProps, ...elementOverrides.props };

		const simpleField: FormKitSchemaComponent = {
			$cmp: "FormKit",
			props,
		};

		return simpleField;
	}

	function xsdElementToFormkitSchema(
		element: XsdElement,
		schema: XsdSchema,
		nestLevel: number,
	): FormKitSchemaNode | FormKitSchemaNode[] | null {
		const [type, complexOrSimple] = findType(element, schema);

		if (complexOrSimple === "complex") {
			return xsdComplexTypeToFormkitSchema(type, element, schema, nestLevel);
		}

		return xsdSimpleTypeToFormKitSchema(type, element);
	}
}

export function useXsdToFormKit(
	xsd: Ref<XsdSchema | undefined>,
	overrides: FormKitSchemaOverrider | Ref<FormKitSchemaOverrider>,
): { schema: Ref<FormKitSchemaNode[]> } {
	const generatedSchema = computed<ReturnType<typeof xsdSchemaToFormkitSchema>>(
		() => xsdSchemaToFormkitSchema(xsd.value, toValue(overrides)),
	);

	const schema = computed<FormKitSchemaNode[]>(
		() => generatedSchema.value?.schema ?? [],
	);

	return {
		schema,
	};
}
